import {useCallback, useRef} from 'react';
import {useOnSelectionChange} from 'reactflow';

import {FlowchartEdge} from '../processFlowchartConfigEditorConstants';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {trackMultiSelectAnalytics} from './useFlowchartStateAnalytics';

const MULTI_SELECT_DEBOUNCE_MS = 1000;

/**
 * Fires multi-select analytics via React Flow's `onSelectionChange` hook.
 * Debounces events so rapid selection churn (e.g., shift-clicking) reports
 * once per interaction instead of per-keystroke.
 */
export function useMultiSelectAnalytics(flowchartTypeName: string): void {
  const multiSelectDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSelectionChange = useCallback(
    ({nodes, edges}: {nodes: Array<FlowchartNode>; edges: Array<FlowchartEdge>}) => {
      if (multiSelectDebounceRef.current) {
        clearTimeout(multiSelectDebounceRef.current);
      }
      multiSelectDebounceRef.current = setTimeout(() => {
        trackMultiSelectAnalytics(nodes, edges, flowchartTypeName);
        multiSelectDebounceRef.current = null;
      }, MULTI_SELECT_DEBOUNCE_MS);
    },
    [flowchartTypeName]
  );
  useOnSelectionChange({onChange: onSelectionChange});
}
